import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PhoneOff,
  Loader2,
  Phone,
  Info,
  AlertTriangle,
  ArrowLeft,
  Send,
  Captions,
  Mic,
  Headphones,
  Hand,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import JesusPortrait from "@/components/JesusPortrait";
import { RealtimeCall } from "@/lib/realtimeOpenAI";
import { ElevenLabsVoiceCall } from "@/lib/voiceElevenLabs";
import { getMe, postUsage, parseQuotaError, postFinalize } from "@/lib/api";
import SignupWall from "@/components/SignupWall";
import TrialBanner from "@/components/TrialBanner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const VOICE_UX_KEY = "jesus.voiceUx";   // localStorage key
const VOICE_UX_HANDS_FREE = "hands-free";
const VOICE_UX_PUSH_TO_TALK = "push-to-talk";

const STATE_LABELS = {
  idle: "Ready",
  connecting: "Calling",
  listening: "Listening",
  recording: "Listening",   // EL pipeline alias — UI keeps the same label
  uploading: "Sending",
  thinking: "Reflecting",
  speaking: "Speaking",
  ended: "Call ended",
  error: "Couldn't open the line",
};

// States that should show the "answered" frame (phone-to-ear).
const ANSWERED_STATES = new Set([
  "connecting", "listening", "recording", "uploading", "thinking", "speaking",
]);
const ACTIVE_STATES = new Set([
  "connecting", "listening", "recording", "uploading", "thinking", "speaking",
]);

function readStoredVoiceUx() {
  if (typeof window === "undefined") return VOICE_UX_HANDS_FREE;
  try {
    const v = window.localStorage.getItem(VOICE_UX_KEY);
    return v === VOICE_UX_PUSH_TO_TALK ? VOICE_UX_PUSH_TO_TALK : VOICE_UX_HANDS_FREE;
  } catch {
    return VOICE_UX_HANDS_FREE;
  }
}

function writeStoredVoiceUx(v) {
  try { window.localStorage.setItem(VOICE_UX_KEY, v); } catch { /* noop */ }
}

export default function Poc() {
  const navigate = useNavigate();
  const [state, setState] = useState("idle");
  const [errorDetail, setErrorDetail] = useState(null);
  const [lastEventType, setLastEventType] = useState("");
  const [config, setConfig] = useState(null); // { voice_mode, voice_label, ... }
  const [captionsOn, setCaptionsOn] = useState(false);
  const [lastTurn, setLastTurn] = useState(null); // {transcript, reply, engine}
  const [fallbackActive, setFallbackActive] = useState(false);
  const [voiceUx, setVoiceUx] = useState(readStoredVoiceUx);
  const [holdActive, setHoldActive] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [fallbackActiveRT, setFallbackActiveRT] = useState(false);  // realtime → EL switch
  // Phase 1: identity + quota state.
  const [me, setMe] = useState(null);
  const [signupWallOpen, setSignupWallOpen] = useState(false);
  const [signupWallReason, setSignupWallReason] = useState("user_initiated"); // or "exhausted"
  const [signupWallDismissible, setSignupWallDismissible] = useState(true);
  const callRef = useRef(null);
  const callKindRef = useRef(null);  // "realtime" | "elevenlabs"
  const callStartedAtRef = useRef(0);
  const closingNoteSentRef = useRef(false);

  const loadMe = async () => {
    const { body } = await getMe();
    if (body) setMe(body);
    return body;
  };
  useEffect(() => { loadMe(); }, []);

  // Persist UX mode on change.
  useEffect(() => {
    writeStoredVoiceUx(voiceUx);
  }, [voiceUx]);

  // Fetch backend voice config once on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/voice/config`);
        if (!r.ok) throw new Error(`config HTTP ${r.status}`);
        const j = await r.json();
        if (alive) setConfig(j);
      } catch (err) {
        if (alive) setConfig({ voice_mode: "elevenlabs", voice_label: "Voice" });
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    return () => {
      try { callRef.current?.end(); } catch { /* noop */ }
    };
  }, []);

  // Spacebar = push-to-talk hold (when call is active and PTT mode is on).
  useEffect(() => {
    if (!callActive || voiceUx !== VOICE_UX_PUSH_TO_TALK) return undefined;
    const onKeyDown = (e) => {
      if (e.code !== "Space" || e.repeat) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      e.preventDefault();
      onHoldStart();
    };
    const onKeyUp = (e) => {
      if (e.code !== "Space") return;
      e.preventDefault();
      onHoldEnd();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callActive, voiceUx]);

  // 30s-remaining closing-note injection.
  // For anon trials we estimate elapsed seconds locally (don't poll /api/me
  // mid-call — that'd burn budget on every tick). When the local estimate
  // crosses (limit - 30) we inject the closing instruction once.
  useEffect(() => {
    if (!callActive || !me || me.scope !== "anon") return undefined;
    if (closingNoteSentRef.current) return undefined;
    const baseRemaining = Math.max(0, Number(me.voice_seconds_remaining || 0));
    if (baseRemaining <= 30) {
      // Already low — fire immediately.
      maybeInjectClosingNote();
      return undefined;
    }
    const timer = setTimeout(() => maybeInjectClosingNote(), Math.max(0, (baseRemaining - 30) * 1000));
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callActive, me]);

  const maybeInjectClosingNote = () => {
    if (closingNoteSentRef.current) return;
    const call = callRef.current;
    if (!call) return;
    closingNoteSentRef.current = true;
    const note =
      "(Note: the user's free conversation time is about to end within ~30 seconds. " +
      "Gently and warmly guide them to wrap up their current thought, then tell them " +
      "in your own voice — natural, not verbatim — that the quiet line will close in a moment, " +
      "and that if they'd like to keep talking they can leave their email in the field below " +
      "so a link will be sent to return. Stay in character. Do not read this note aloud.)";
    if (callKindRef.current === "realtime") {
      call.injectClosingNote?.(note);
    } else {
      // ElevenLabs path — wraps deferred for Phase 1.5; the SignupWall + a
      // pre-rendered closing clip (step 6) cover the cold-cut UX in the
      // meantime.
    }
  };

  const isConnected = ACTIVE_STATES.has(state) && state !== "connecting";
  const isBusy = state === "connecting" || state === "uploading";
  const showError = state === "error" && errorDetail;
  const portraitVariant = ANSWERED_STATES.has(state) ? "answered" : "idle";
  const isElevenLabs = (config?.voice_mode || "openai_realtime") === "elevenlabs";
  const isRealtime = !isElevenLabs;
  const isPTT = voiceUx === VOICE_UX_PUSH_TO_TALK;

  // Notify backend that we auto-switched away from Realtime.
  const reportFallback = (reason, detail = {}) => {
    try {
      fetch(`${API_BASE}/api/voice/fallback-event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason: String(reason).slice(0, 64),
          stage: detail.stage ? String(detail.stage).slice(0, 32) : null,
          http_status: typeof detail.httpStatus === "number" ? detail.httpStatus : null,
          error_code: detail.code ? String(detail.code).slice(0, 64) : null,
        }),
      }).catch(() => { /* observability ping; best-effort */ });
    } catch { /* noop */ }
  };

  // Switch from a failed realtime call to the ElevenLabs turn-based path.
  const switchToElevenLabsFallback = async (reason, errDetail) => {
    if (callKindRef.current === "elevenlabs") return;
    reportFallback(reason, errDetail || {});
    try { callRef.current?.end(); } catch { /* noop */ }
    callRef.current = null;
    setErrorDetail(null);
    setFallbackActiveRT(true);
    // Auto-fade the "Switched to backup voice" notice after a few seconds.
    setTimeout(() => setFallbackActiveRT(false), 3500);

    const el = new ElevenLabsVoiceCall({
      apiBase: API_BASE,
      mode: isPTT ? "manual" : "auto",
      onState: (s) => setState(s),
      onError: (e) => setErrorDetail(e),
      onTurn: ({ transcript, reply, engine }) => {
        setLastTurn({ transcript, reply, engine });
        setFallbackActive(engine === "openai_fallback");
      },
    });
    callRef.current = el;
    callKindRef.current = "elevenlabs";
    setState("connecting");
    await el.start();
  };

  // Lazily build a call instance with the right mode for the current UX.
  const ensureCall = async () => {
    if (callRef.current) return callRef.current;

    // Centralised error router for both call clients. Any 402 from the
    // session/turn endpoints lands here.
    const wrapError = (e) => {
      const status = e?.httpStatus ?? e?.status ?? 0;
      if (status === 402) {
        const scope = (e?.body?.detail?.scope) || (parseQuotaError(e?.body)?.scope) || "anon";
        onQuotaExhausted(scope);
        return;
      }
      setErrorDetail(e);
    };

    if (isRealtime) {
      const call = new RealtimeCall({
        apiBase: API_BASE,
        mode: isPTT ? "ptt" : "vad",
        onState: (s) => setState(s),
        onEvent: (m) => setLastEventType(m?.type || ""),
        onError: (e) => {
          if (callKindRef.current === "realtime" && (e?.httpStatus !== 402)) {
            switchToElevenLabsFallback("realtime_error", e);
          } else {
            wrapError(e);
          }
        },
      });
      callRef.current = call;
      callKindRef.current = "realtime";
      setCallActive(true);
      await call.start();
      return call;
    }
    const call = new ElevenLabsVoiceCall({
      apiBase: API_BASE,
      mode: isPTT ? "manual" : "auto",
      onState: (s) => setState(s),
      onError: wrapError,
      onTurn: ({ transcript, reply, engine }) => {
        setLastTurn({ transcript, reply, engine });
        setFallbackActive(engine === "openai_fallback");
        // Refresh quota — server already deducted on this turn.
        loadMe();
      },
    });
    callRef.current = call;
    callKindRef.current = "elevenlabs";
    setCallActive(true);
    setState("connecting");
    await call.start();
    return call;
  };

  // Hands-free: starts the auto-VAD loop.
  const onCall = async () => {
    setErrorDetail(null);
    setLastEventType("");
    setLastTurn(null);
    setFallbackActive(false);
    setFallbackActiveRT(false);
    closingNoteSentRef.current = false;
    callStartedAtRef.current = Date.now();
    try {
      await ensureCall();
    } catch (err) {
      // Catches the 402 thrown by ensureCall via the realtime/EL session POST.
      // (Both clients surface 402 as a stage:"session" httpStatus:402 onError.)
      handleQuotaErrorFromException(err);
    }
  };

  const onHangup = () => {
    // Capture session id + history from whichever client is active before we tear it down.
    let sessionId = null;
    let transcript = [];
    try {
      const inst = callRef.current;
      if (inst) {
        if (inst.sessionId) sessionId = inst.sessionId;
        if (Array.isArray(inst.history)) transcript = inst.history.slice();
      }
    } catch { /* noop */ }
    let elapsedSeconds = 0;
    // Post the elapsed Realtime call duration to the quota service. The EL
    // path deducts server-side at end of each turn, so it doesn't need this.
    if (callKindRef.current === "realtime" && callStartedAtRef.current) {
      const elapsed = Math.max(0, (Date.now() - callStartedAtRef.current) / 1000);
      elapsedSeconds = elapsed;
      if (elapsed > 0) {
        const sid = `rt-${callStartedAtRef.current}`;
        if (!sessionId) sessionId = sid;
        postUsage(sid, elapsed, "openai_realtime", "end").catch(() => { /* noop */ });
      }
    } else if (callStartedAtRef.current) {
      elapsedSeconds = Math.max(0, (Date.now() - callStartedAtRef.current) / 1000);
    }
    // Fire-and-forget finalize → backend schedules journal + memory bg work.
    // For authed users only; backend short-circuits anon callers.
    if (sessionId) {
      postFinalize(
        sessionId,
        transcript,
        Math.round(elapsedSeconds),
        callKindRef.current === "realtime" ? "openai_realtime" : "elevenlabs",
      ).catch(() => { /* noop */ });
    }
    callStartedAtRef.current = 0;
    closingNoteSentRef.current = false;
    try { callRef.current?.end(); } catch { /* noop */ }
    callRef.current = null;
    callKindRef.current = null;
    setCallActive(false);
    setHoldActive(false);
    setFallbackActiveRT(false);
    // Reload quota for the banner.
    loadMe();
  };

  // Map a thrown 402 (or one piggy-backed onto an onError detail) to the
  // SignupWall / quota-exhausted notice.
  const handleQuotaErrorFromException = (err) => {
    const httpStatus = err?.httpStatus ?? err?.status ?? 0;
    const detail = err?.detail || err?.body?.detail || err?.body || null;
    const quota = parseQuotaError(detail) || parseQuotaError(err);
    if (httpStatus === 402 || quota) {
      onQuotaExhausted(quota?.scope || "anon");
    } else {
      // Not a quota error — let the existing error banner show it.
      setErrorDetail(err);
    }
  };

  const onQuotaExhausted = (scope) => {
    try { callRef.current?.end(); } catch { /* noop */ }
    callRef.current = null;
    callKindRef.current = null;
    setCallActive(false);
    setHoldActive(false);
    if (scope === "user") {
      // Authed user — show a calmer notice via toast, no wall.
      toast(
        "You've reached today's 5 minutes. Come back tomorrow — or whenever you're ready.",
        { duration: 5500 }
      );
      loadMe();
      return;
    }
    // Anon — open the (non-dismissible) SignupWall.
    setSignupWallReason("exhausted");
    setSignupWallDismissible(false);
    setSignupWallOpen(true);
    loadMe();
  };

  const onUserClickSignup = () => {
    setSignupWallReason("user_initiated");
    setSignupWallDismissible(true);
    setSignupWallOpen(true);
  };

  const onSendNow = () => {
    try { callRef.current?.endTurn?.(); } catch { /* noop */ }
  };

  // Push-to-talk handlers — attached to the Hold-to-Speak button. Works on
  // both engines: realtime uses beginTurn / commitTurn (data-channel ops),
  // ElevenLabs uses recordTurnManual / stopRecordTurnManual (MediaRecorder).
  const onHoldStart = async () => {
    if (state === "recording" || state === "uploading" || state === "thinking" || state === "speaking") {
      // Realtime supports clean interruption while speaking, but a still-busy
      // network turn (uploading/thinking) is left alone to avoid races.
      if (!(callKindRef.current === "realtime" && state === "speaking")) {
        return;
      }
    }
    setErrorDetail(null);
    setHoldActive(true);
    const call = await ensureCall();
    if (!call) return;
    if (callKindRef.current === "realtime") {
      call.beginTurn?.();
    } else if (call.recordTurnManual) {
      call.recordTurnManual();
    }
  };

  const onHoldEnd = () => {
    if (!holdActive) return;
    setHoldActive(false);
    const call = callRef.current;
    if (!call) return;
    if (callKindRef.current === "realtime") {
      // No 300ms guard on realtime — server will simply produce a short
      // turn or no audio if the user barely held.
      call.commitTurn?.();
    } else if (call.stopRecordTurnManual) {
      const result = call.stopRecordTurnManual({ minMs: 300 });
      if (result?.tooShort) {
        toast("Hold a bit longer to speak.", { duration: 1800 });
      }
    }
  };

  return (
    <main
      data-testid="poc-page"
      className="relative min-h-screen overflow-hidden paper-grain"
    >
      {/* Halos */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-32 h-[520px] w-[520px] rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(closest-side, hsl(150 30% 70% / 0.55), transparent 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-48 -left-40 h-[560px] w-[560px] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(closest-side, hsl(220 35% 45% / 0.35), transparent 70%)" }}
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-3xl flex-col px-5 py-7 sm:px-10 sm:py-10">
        {/* Header */}
        <header
          className="flex items-center justify-between gap-3"
          data-testid="poc-header"
        >
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="poc-back-btn"
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[11px] uppercase tracking-[0.22em] text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Back to home"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Home
          </button>

          <div className="flex items-center gap-2">
            {!callActive && (
              <VoiceUxToggle
                value={voiceUx}
                onChange={setVoiceUx}
              />
            )}
            <TrialBanner me={me} active={callActive} onSignup={onUserClickSignup} />
            <StatusPill state={state} />
            <CaptionsToggle on={captionsOn} setOn={setCaptionsOn} />
            <DisclosurePopover />
          </div>
        </header>

        {/* Center stage */}
        <section
          className="flex flex-1 flex-col items-center justify-center gap-7 py-6 sm:gap-9 sm:py-10"
          data-testid="poc-stage"
        >
          <PortraitStage
            variant={portraitVariant}
            state={state}
          />

          <Subtitle state={state} />

          <CallControls
            state={state}
            isConnected={isConnected}
            isBusy={isBusy}
            onCall={onCall}
            onHangup={onHangup}
            onSendNow={onSendNow}
            isElevenLabs={isElevenLabs}
            isPTT={isPTT}
            callActive={callActive}
            holdActive={holdActive}
            onHoldStart={onHoldStart}
            onHoldEnd={onHoldEnd}
          />

          {captionsOn && lastTurn && (
            <CaptionsBlock turn={lastTurn} />
          )}

          {fallbackActive && !showError && (
            <FallbackNotice voiceLabel={config?.voice_label || "primary voice"} />
          )}

          {fallbackActiveRT && !showError && (
            <p
              data-testid="poc-realtime-fallback-notice"
              className="text-[12px] italic text-muted-foreground/80 transition-opacity duration-500"
            >
              Switched to backup voice.
            </p>
          )}

          {showError && <ErrorBanner detail={errorDetail} />}

          {isConnected && lastEventType && (
            <p
              data-testid="poc-last-event"
              className="font-mono text-[11px] tracking-tight text-muted-foreground/70"
            >
              {lastEventType}
            </p>
          )}
        </section>

        {/* Footer — engine label only, no big disclaimer */}
        <footer className="mt-auto pt-6" data-testid="poc-footer">
          <p className="text-[11px] text-muted-foreground/60">
            voice ·{" "}
            <code className="font-mono">
              {callKindRef.current === "elevenlabs"
                ? "elevenlabs"
                : (config?.voice_mode || "…")}
            </code>
            {isRealtime && config?.openai_realtime_model && (
              <span className="ml-1 text-muted-foreground/50">
                · {config.openai_realtime_model}
              </span>
            )}
            {fallbackActive && (
              <span className="ml-2 text-muted-foreground/70">
                (fallback active)
              </span>
            )}
          </p>
        </footer>
      </div>

      {/* Trial-end / sign-up wall — covers everything when open. */}
      <SignupWall
        open={signupWallOpen}
        reason={signupWallReason}
        dismissible={signupWallDismissible}
        onDismiss={() => setSignupWallOpen(false)}
        onSent={() => { /* keep wall open with the "check your email" view */ }}
      />
    </main>
  );
}

// (the rest of this file is internal subcomponents — VoiceUxToggle, etc.)

function VoiceUxToggle({ value, onChange }) {
  const isPTT = value === VOICE_UX_PUSH_TO_TALK;
  return (
    <div
      data-testid="poc-voice-ux-toggle"
      role="radiogroup"
      aria-label="Voice mode"
      className="inline-flex h-7 items-center rounded-full border border-border bg-card/60 p-0.5 text-[10px] uppercase tracking-[0.18em] backdrop-blur"
    >
      <button
        type="button"
        role="radio"
        aria-checked={!isPTT}
        data-testid="poc-voice-ux-hands-free"
        onClick={() => onChange(VOICE_UX_HANDS_FREE)}
        className={
          "inline-flex h-6 items-center gap-1 rounded-full px-2.5 transition-colors " +
          (!isPTT
            ? "bg-[hsl(var(--accent))]/30 text-foreground"
            : "text-muted-foreground/70 hover:text-foreground")
        }
      >
        <Headphones className="h-3 w-3" />
        <span>Hands-free</span>
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={isPTT}
        data-testid="poc-voice-ux-push-to-talk"
        onClick={() => onChange(VOICE_UX_PUSH_TO_TALK)}
        className={
          "inline-flex h-6 items-center gap-1 rounded-full px-2.5 transition-colors " +
          (isPTT
            ? "bg-[hsl(var(--accent))]/30 text-foreground"
            : "text-muted-foreground/70 hover:text-foreground")
        }
      >
        <Hand className="h-3 w-3" />
        <span>Push-to-Talk</span>
      </button>
    </div>
  );
}

function CaptionsToggle({ on, setOn }) {
  return (
    <button
      type="button"
      onClick={() => setOn(!on)}
      data-testid="poc-captions-toggle"
      aria-pressed={on}
      aria-label={on ? "Hide captions" : "Show captions"}
      className={
        "inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-[11px] uppercase tracking-[0.18em] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
        (on
          ? "bg-[hsl(var(--accent))]/25 text-foreground"
          : "text-muted-foreground/60 hover:text-foreground")
      }
    >
      <Captions className="h-3.5 w-3.5" />
      <span>{on ? "On" : "CC"}</span>
    </button>
  );
}

function CaptionsBlock({ turn }) {
  return (
    <div
      data-testid="poc-captions"
      className="w-full max-w-md space-y-2 text-center"
    >
      {turn.transcript && (
        <p
          data-testid="poc-captions-user"
          className="text-[12px] text-muted-foreground/75"
        >
          <span className="uppercase tracking-[0.18em] text-muted-foreground/55 mr-1.5">
            you
          </span>
          {turn.transcript}
        </p>
      )}
      {turn.reply && (
        <p
          data-testid="poc-captions-reply"
          className="font-serif italic text-[13px] text-foreground/80"
        >
          {turn.reply}
        </p>
      )}
    </div>
  );
}

function FallbackNotice({ voiceLabel }) {
  return (
    <p
      data-testid="poc-fallback-notice"
      className="max-w-md text-center text-[11px] italic text-muted-foreground/75"
    >
      Warmer voice unavailable — using fallback voice for this turn.
    </p>
  );
}

function PortraitStage({ variant, state }) {
  const showRings = state === "listening";
  return (
    <div
      data-testid="poc-portrait-stage"
      className="relative flex w-full items-center justify-center"
    >
      {/* Listening rings — soft sage circles around the portrait */}
      {showRings && (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute h-[78%] max-h-[420px] w-[78%] max-w-[420px] rounded-full border border-[hsl(var(--accent))]/55 listen-ring"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute h-[78%] max-h-[420px] w-[78%] max-w-[420px] rounded-full border border-[hsl(var(--accent))]/55 listen-ring"
            style={{ animationDelay: "0.7s" }}
          />
        </>
      )}

      <JesusPortrait
        variant={variant}
        size="lg"
        breathing={state === "speaking"}
        glow={variant === "answered"}
        testId="poc-portrait"
      />
    </div>
  );
}

function CallControls({
  state, isConnected, isBusy, onCall, onHangup, onSendNow, isElevenLabs,
  isPTT, callActive, holdActive, onHoldStart, onHoldEnd,
}) {
  const isRecording = state === "recording" || state === "listening";

  // Push-to-talk mode (ElevenLabs only): once the call is active, render the
  // Hold-to-Speak button + End-call. Hands-free auto-VAD UI is preserved
  // exactly as before for the !isPTT branch.
  if (isPTT && callActive) {
    return (
      <div className="flex flex-col items-center gap-4">
        <HoldToSpeakButton
          state={state}
          holdActive={holdActive}
          onHoldStart={onHoldStart}
          onHoldEnd={onHoldEnd}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onHangup}
          data-testid="poc-end-call-btn"
          className="rounded-full px-5 text-destructive/80 hover:text-destructive hover:bg-destructive/5"
        >
          <PhoneOff className="mr-2 h-3.5 w-3.5" />
          End call
        </Button>
        <p
          data-testid="poc-ptt-hint"
          className="text-[11px] text-muted-foreground/60"
        >
          Hold the button (or Space) while you speak.
        </p>
      </div>
    );
  }

  if (isConnected) {
    return (
      <div className="flex flex-wrap items-center justify-center gap-3">
        {isElevenLabs && isRecording && (
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={onSendNow}
            data-testid="poc-send-now-btn"
            className="rounded-full px-5 border-[hsl(var(--accent))]/40 text-foreground hover:bg-[hsl(var(--accent))]/10"
          >
            <Send className="mr-2 h-4 w-4" />
            Send now
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={onHangup}
          data-testid="poc-end-call-btn"
          className="rounded-full px-7 border-destructive/40 text-destructive hover:bg-destructive/5"
        >
          <PhoneOff className="mr-2 h-4 w-4" />
          End call
        </Button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onCall}
      disabled={isBusy}
      data-testid="poc-call-btn"
      aria-label="Start call"
      className="group inline-flex items-center gap-3 rounded-full bg-[hsl(var(--primary))] px-7 py-4 text-base font-medium text-[hsl(var(--primary-foreground))] shadow-[0_24px_60px_-22px_hsl(var(--primary)/0.55)] transition-transform duration-300 hover:scale-[1.02] active:scale-[0.99] disabled:opacity-70"
    >
      <span className="grid h-9 w-9 place-items-center rounded-full bg-[hsl(var(--primary-foreground))]/12 transition-colors duration-300 group-hover:bg-[hsl(var(--primary-foreground))]/20">
        {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
      </span>
      {isBusy ? "Calling…" : (state === "ended" ? "Call again" : "Call Jesus")}
    </button>
  );
}

function HoldToSpeakButton({ state, holdActive, onHoldStart, onHoldEnd }) {
  const isRecording = state === "recording" && holdActive;
  const isThinking  = state === "thinking" || state === "uploading";
  const isSpeaking  = state === "speaking";
  const disabled    = isThinking || isSpeaking;

  // Pointer events cover mouse, touch, and pen uniformly. We also wire
  // pointercancel + pointerleave so the user releasing outside the button
  // (or a backgrounded tab) doesn't leave the recorder running.
  const handlers = {
    onPointerDown: (e) => {
      if (disabled) return;
      e.preventDefault();
      try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
      onHoldStart();
    },
    onPointerUp: (e) => {
      e.preventDefault();
      try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* noop */ }
      onHoldEnd();
    },
    onPointerCancel: () => onHoldEnd(),
    onPointerLeave:  () => { if (holdActive) onHoldEnd(); },
  };

  let label = "Hold to speak";
  if (isRecording) label = "Listening — keep holding";
  else if (isThinking) label = "Reflecting…";
  else if (isSpeaking) label = "Wait for reply…";

  return (
    <button
      type="button"
      data-testid="poc-hold-to-speak-btn"
      data-recording={isRecording ? "true" : "false"}
      aria-pressed={isRecording}
      aria-label="Hold to speak"
      disabled={disabled}
      {...handlers}
      className={
        "group relative inline-flex select-none items-center gap-3 rounded-full px-8 py-5 text-base font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
        (isRecording
          ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] shadow-[0_22px_60px_-16px_hsl(var(--accent)/0.7)] scale-[1.02]"
          : disabled
          ? "bg-muted/60 text-muted-foreground/70 cursor-not-allowed"
          : "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-[0_24px_60px_-22px_hsl(var(--primary)/0.55)] hover:scale-[1.02] active:scale-[0.99]")
      }
    >
      <span className="grid h-9 w-9 place-items-center rounded-full bg-white/15">
        {isThinking ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Mic className="h-4 w-4" />
        )}
      </span>
      <span data-testid="poc-hold-to-speak-label">{label}</span>
    </button>
  );
}

function Subtitle({ state }) {
  const text = useMemo(() => {
    switch (state) {
      case "idle":      return "Tap to begin a quiet, spoken conversation.";
      case "connecting":return "Opening a line — one moment.";
      case "listening": return "He's listening. Speak whenever you're ready.";
      case "recording": return "He's listening. Speak whenever you're ready.";
      case "uploading": return "Sending your words…";
      case "thinking":  return "Reflecting on what you said…";
      case "speaking":  return "Speaking with you now.";
      case "ended":     return "The call has ended. Tap to begin again.";
      case "error":     return "We couldn't open the line — see the note below.";
      default:          return "";
    }
  }, [state]);

  return (
    <p
      data-testid="poc-subtitle"
      className="max-w-md text-center font-serif italic text-base md:text-lg text-muted-foreground"
    >
      {text}
    </p>
  );
}

function StatusPill({ state }) {
  const tone =
    state === "error" ? "destructive"
    : state === "listening" ? "accent"
    : state === "speaking" ? "primary"
    : "muted";

  const dotClass = {
    accent: "bg-[hsl(var(--accent))]",
    primary: "bg-[hsl(var(--primary))]",
    destructive: "bg-[hsl(var(--destructive))]",
    muted: "bg-muted-foreground/60",
  }[tone];

  return (
    <div
      data-testid="poc-status-pill"
      className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs text-muted-foreground backdrop-blur"
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} aria-hidden />
      <span data-testid="poc-status-label">{STATE_LABELS[state] || state}</span>
    </div>
  );
}

function DisclosurePopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="About this companion"
          data-testid="poc-disclosure-trigger"
          className="inline-grid h-7 w-7 place-items-center rounded-full text-muted-foreground/60 transition-colors hover:text-foreground hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 text-[13px] leading-relaxed"
        data-testid="poc-disclosure-popover"
      >
        <p data-testid="poc-disclosure-text" className="text-foreground/90">
          <span className="italic">Jesus</span> is an AI companion inspired by the
          teachings, voice, and compassion of Jesus in the New Testament. It is
          for reflection, prayer, and scripture-rooted conversation. It may be
          incomplete or wrong. It is not medical, mental-health, legal, financial,
          or emergency advice. If you are in crisis, please contact your local
          emergency services or a crisis hotline.
        </p>
      </PopoverContent>
    </Popover>
  );
}

function ErrorBanner({ detail }) {
  const tag = useMemo(() => {
    const parts = [];
    if (detail.stage === "sdp" || detail.stage === "session") parts.push(detail.stage.toUpperCase());
    if (detail.stage === "openai") parts.push("OpenAI");
    if (detail.httpStatus) parts.push(`HTTP ${detail.httpStatus}`);
    if (detail.code) parts.push(detail.code);
    else if (detail.openaiType) parts.push(detail.openaiType);
    return parts.join(" · ");
  }, [detail]);

  return (
    <div
      role="alert"
      data-testid="poc-error"
      className="w-full max-w-lg rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div className="flex-1 space-y-1.5">
          <p className="font-medium" data-testid="poc-error-headline">{detail.headline}</p>
          {tag && (
            <p
              className="font-mono text-[11px] tracking-tight text-destructive/80"
              data-testid="poc-error-tag"
            >
              {tag}
            </p>
          )}
          {detail.message && detail.message !== detail.headline && (
            <p className="text-xs text-destructive/80" data-testid="poc-error-message">
              {detail.message}
            </p>
          )}
          {detail.snippet && (
            <details className="mt-1">
              <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-destructive/70">
                upstream details
              </summary>
              <pre
                data-testid="poc-error-snippet"
                className="mt-1.5 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-destructive/5 p-2 text-[11px] font-mono leading-relaxed text-destructive/80"
              >
                {detail.snippet}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
